import type { LucideIcon } from 'lucide-react'

export interface TenantGroupDTO {
  id: string
  code: string
  name: string
  type: string
  availabilityBuildEnabled: boolean
  createdAt: string
  updatedAt: string
}

export interface TenantDTO {
  id: string
  code: string
  name: string
  tenantGroupId: string
  createdAt: string
  updatedAt: string
}

// Optional, free-form string -> string bag. No keys are required.
export type ConfigTags = Record<string, string>

// A single editable key/value row in the tags editor and tag filter.
export interface TagRow {
  key: string
  value: string
}

// Applied filters for the tenant-config list page. tenant/isp/venue/configKey
// are single exact-match values (empty string = unset); tags is a list of
// `key:value` pairs. All AND-combine server-side. ConfigsPage bridges these
// friendly names to the API/URL params (tenantCodes,
// inventorySystemProfileCodes, venueIds, configKeys, tags); useConfigs sends them.
export interface ConfigFilters {
  tenant: string
  isp: string
  venue: string
  configKey: string
  tags: string[]
}

// A single option in a <select> dropdown: the submitted value plus its label.
export interface SelectOption {
  value: string
  label: string
}

export interface ConfigDTO {
  id: string
  configKey: string
  configValue: string
  configType: string
  tenantCode: string | null
  inventorySystemProfileCode: string | null
  venueId: string | null
  tags: ConfigTags
  visibleToFrontend: boolean
  createdAt: string
  updatedAt: string
}

export enum InventorySystemTypes {
  Av = 'av',
  Ingresso = 'ingresso',
  Nliven = 'nliven',
  Telecharge = 'telecharge',
  Spektrix = 'spektrix',
  Lineup = 'lineup',
  Enta = 'enta',
  Tessitura = 'tessitura',
  Passport = 'passport',
}

export enum ConfigScope {
  IspConfig = 'ISP Config',
  TenantConfig = 'Tenant Config',
  TenantIspConfig = 'Tenant ISP Config',
  TenantVenueConfig = 'Tenant Venue Config',
}

export enum BuildStatus {
  New = 'new',
  Idle = 'idle',
  Queued = 'queued',
  Running = 'running',
  Stopped = 'stopped',
}

export interface InventorySystemProfileDTO {
  id: string
  code: string
  name: string
  inventorySystemType: InventorySystemTypes
  buildSchedule: string
  buildStatus: BuildStatus
  availabilityBuildEnabled: boolean
  tenantGroupId: string
  createdAt: string
  updatedAt: string
}

interface FieldDefBase {
  name: string
  label: string
  required?: boolean
  isDisabled?: boolean
  validate?: (value: unknown) => string | null
  deriveValues?: (value: unknown) => Record<string, unknown>
}

interface TextFieldDef extends FieldDefBase {
  type: 'text'
  defaultValue?: string
}

interface CronFieldDef extends FieldDefBase {
  type: 'cron'
  defaultValue?: string
}

interface NumberFieldDef extends FieldDefBase {
  type: 'number'
  defaultValue?: number
}

interface CheckboxFieldDef extends FieldDefBase {
  type: 'checkbox'
  defaultValue?: boolean
}

interface SelectFieldDef extends FieldDefBase {
  type: 'select'
  options: { label: string; value: string }[]
  defaultValue?: string
  valueType?: 'string' | 'number'
  searchable?: boolean
}

export type FieldDef =
  | TextFieldDef
  | CronFieldDef
  | NumberFieldDef
  | CheckboxFieldDef
  | SelectFieldDef

export interface VenueDTO {
  id: string
  sysVenueId: string
  status: string
  name: string
  venueSlug: string
  timezone: string
  latitude: number
  longitude: number
  type: string
  inventorySystemType: InventorySystemTypes
  createdAt: string
  updatedAt: string
}

export interface InventorySystemProfileToVenueDTO {
  venueId: string
  inventorySystemProfileId: string
  tenantGroupId: string
}

export interface DetailFieldDef {
  label: string
  value: React.ReactNode
}

export interface ConfigScopeFields {
  tenantCode: string | null
  inventorySystemProfileCode: string | null
  venueId: string | null
}

export interface ConfigIdFields {
  configKey: string
  tenantCode: string | null
  inventorySystemProfileCode: string | null
  venueId: string | null
}

export type EditRowDef =
  | { mode: 'readonly'; label: string; value: React.ReactNode }
  | { mode: 'editable'; field: FieldDef; initialValue: unknown }

// Request body for POST /v1/act/build/inventory. The endpoint has two modes,
// selected by the presence of `sysEventIds`: event-scoped (sysEventIds present,
// inventorySystemType required) vs ISP-scoped (no sysEventIds, at least one of
// tenantGroupCodes / inventorySystemType required). See buildRebuildInventoryPayload.
export interface RebuildInventoryPayload {
  tenantGroupCodes?: string[]
  inventorySystemType?: string
  sysEventIds?: string[]
  rebuildFilterPrices?: boolean
}

// Outcome of an act endpoint call, mapped to a distinct alert style in ActionFeedback.
export type ActionFeedbackKind = 'success' | 'conflict' | 'notFound' | 'error'

export interface ActionFeedback {
  kind: ActionFeedbackKind
  message: string
}

export interface NavigationItem {
  label: string
  to: string
  icon: LucideIcon
  singularLabel?: string
}

export interface NavigationGroup {
  label: string
  items: readonly NavigationItem[]
}

export interface NavigationMatch {
  group: NavigationGroup
  item: NavigationItem
}

export interface BreadcrumbItem {
  label: string
  to?: string
}

export type ListFilterValues = Readonly<Record<string, string>>

export type ListFilterFieldDef =
  | { key: string; label: string; kind: 'text'; placeholder: string }
  | {
      key: string
      label: string
      kind: 'select'
      options: readonly SelectOption[]
      searchMode: 'contains' | 'none'
    }

export interface VenueInventorySystemProfileLink {
  inventorySystemProfileId: number
  tenantGroupId: number
}
